import { useCallback, useEffect, useState } from 'react';
import 'react-datepicker/dist/react-datepicker.css';
import SearchInput from '@components/common/SearchInput';
import { api } from '@/api/growth/season/api';
import {
    SeasonPopListReq,
    SeasonPopListItem,
    SeasonPopListRes,
} from '@/api/growth/season/type';
import DataTable, {
    ColumnConfig,
} from '@components/common/DataTable/DataTable';
import ActionButton from '@components/common/ActionButton/ActionButton';

interface SeasonPopListProps<SeasonPopListItem> {
    onClickRow: (row: SeasonPopListItem) => void;
}

const SeasonPopList = ({
    onClickRow,
}: SeasonPopListProps<SeasonPopListItem>) => {
    const [filters, setFilters] = useState<SeasonPopListReq>(() => {
        return {
            pageSize: 5,
            nowPage: 1,
            pageGroup: 5,
            keyword: '',
        };
    });

    const updateFilter = (key: keyof SeasonPopListReq, value: any) => {
        setFilters(prev => ({
            ...prev,
            [key]: value,
            ...(key !== 'nowPage' && { nowPage: 1 }),
        }));
    };

    const handleSearch = (value: string) => {
        updateFilter('keyword', value);
    };

    const handlePageChange = (page: number) => {
        updateFilter('nowPage', page);
    };

    const handlePageSizeChange = (pageSize: number) => {
        updateFilter('pageSize', pageSize);
    };

    const [seasonRes, setSeasonListRes] = useState<SeasonPopListRes>();

    const fetchSeasonList = useCallback(async () => {
        const response = await api.seasonPopList(filters);
        setSeasonListRes(response);
    }, [filters]);

    const VALID_STATUS_MAP = {
        true: { text: '진행중', className: 'bg-success' },
        false: { text: '종료됨', className: 'bg-danger' },
    } as const;

    const columns: ColumnConfig<SeasonPopListItem>[] = [
        { key: 'gsIdx', header: 'No' },
        { key: 'userName', header: '대행사명', clickable: true },
        { key: 'userId', header: '대행사ID', clickable: true },
        { key: 'name', header: '작기명', clickable: true },
        { key: 'startDt', header: '작기 시작' },
        { key: 'endDt', header: '작기 종료' },
        {
            key: 'isValid',
            header: '진행여부',
            render: value => {
                const statusInfo =
                    VALID_STATUS_MAP[value as keyof typeof VALID_STATUS_MAP];
                return (
                    <span className={`badge ${statusInfo.className}`}>
                        {statusInfo.text}
                    </span>
                );
            },
        },
        { key: 'farmlandCnt', header: '농지수', render: value => value || '-' },
        {
            key: 'cultivationArea',
            header: '면적(㎡)',
            render: value => value.toLocaleString() || '-',
        },

        {
            key: 'actions', // 직접 액션 컬럼 정의
            header: '선택',
            clickable: false,
            render: (_value, row) => (
                <ActionButton<SeasonPopListItem>
                    type="save"
                    size="sm"
                    text="선택"
                    className="btn-danger-light"
                    onClick={() => handleBtnClick(row)}
                />
            ),
        },
    ];

    // 액션 버튼 핸들러들
    const handleBtnClick = (row: SeasonPopListItem) => {
        onClickRow(row);
    };

    useEffect(() => {
        fetchSeasonList();
    }, [fetchSeasonList]);

    return (
        <>
            <div className="row">
                <div className="col-xl-12">
                    <div className="card">
                        <div className="card-body">
                            <div className="row">
                                <div className="col-12">
                                    <SearchInput
                                        placeholder="작기명을 입력해주세요."
                                        value={filters.keyword || ''}
                                        onSearch={handleSearch}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="row ">
                <div className="col-xl-12">
                    {seasonRes && (
                        <DataTable<SeasonPopListItem>
                            data={seasonRes?.data}
                            columns={columns}
                            paging={seasonRes?.paging}
                            onPageChange={handlePageChange}
                            onPageSizeChange={handlePageSizeChange}
                            showHeader={false}
                            cardClassName={'board-0 mb-0 shadow-none'}
                            noneFooterPadding={true}
                            onClickRow={onClickRow}
                        />
                    )}
                </div>
            </div>
        </>
    );
};

export default SeasonPopList;
